/**
 * /admin/omie/ativar-write — conferência final antes de liberar o envio de
 * dados pro Omie (Camada 7 / R-IMPORT 3 A1.9).
 *
 * O formulário só CONFERE (2 confirmações + texto de segurança + código de
 * verificação). O backend valida em POST /api/v1/admin/omie/ativar-write.
 * Mesmo passando, quem cuida do servidor ainda precisa ajustar
 * OMIE_WRITE_ENABLED no `.env` — de propósito, pra não existir um único
 * botão capaz de ligar a gravação no financeiro.
 *
 * FASE-C (redesign): "⚠️ Ativar WRITE", "Camada 7 — 6 checks + 2FA", `bg-white`
 * cru e os blocos ✅/❌ com JSON solto saíram. Ficou tudo em componentes
 * canônicos, com a instrução de `.env` dentro de um Alert e o detalhe técnico
 * recolhido num "Detalhes técnicos".
 */
import { useState } from 'react';

import {
  Alert,
  Button,
  Card,
  Checkbox,
  Input,
  Label,
  PageHeader,
} from '@/components/ui';
import { api } from '@/lib/api';

const EXPECTED_TYPE2_TEXT = 'EU SEI O QUE ESTOU FAZENDO - ATIVAR WRITE';

export function OmieAtivarWritePage() {
  const [conf10Clientes, setConf10Clientes] = useState(false);
  const [conf10Produtos, setConf10Produtos] = useState(false);
  const [type2Text, setType2Text] = useState('');
  const [totp, setTotp] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<unknown>(null);
  const [err, setErr] = useState<{
    error: string;
    code?: string;
    message?: string;
    checks?: unknown;
  } | null>(null);

  async function submit() {
    setSubmitting(true);
    setErr(null);
    setResult(null);
    try {
      const r = await api.post<unknown>('/api/v1/admin/omie/ativar-write', {
        conferenciaManual10Clientes: conf10Clientes,
        conferenciaManual10Produtos: conf10Produtos,
        type2ConfirmationText: type2Text,
        totpCode: totp,
      });
      setResult(r);
    } catch (e) {
      const apiErr = e as { response?: { error: string; code?: string; message?: string; checks?: unknown } };
      setErr(
        apiErr.response ?? { error: 'erro', message: (e as Error).message },
      );
    } finally {
      setSubmitting(false);
    }
  }

  // LAYOUT-CANONICO: o `max-w-2xl` da raiz saiu — padding/largura agora vêm da
  // casca (o AdminLayout envolve nav + Outlet num PageContainer).
  return (
    <div className="min-w-0 space-y-5" data-testid="omie-ativar-write-page">
      <PageHeader
        className="mb-0"
        crumb="Configurações · Integrações"
        title="Liberar envio de dados ao Omie"
        subtitle="Enquanto o envio está desligado, o IGM só lê o Omie. Esta tela confere se está tudo pronto pra que ele também possa gravar."
      />

      <Alert variant="warning" title="Esta tela não liga nada sozinha">
        Ela só confere se as condições foram cumpridas. Mesmo passando na conferência, quem
        cuida do servidor ainda precisa mudar <code>OMIE_WRITE_ENABLED</code> para{' '}
        <code>on</code> no arquivo <code>.env</code>. É proposital: sem esse segundo passo,
        um clique errado aqui não consegue gravar nada no financeiro.
      </Alert>

      <Card variant="flat" className="p-4 sm:p-6 space-y-5">
        <Checkbox
          checked={conf10Clientes}
          onChange={(e) => setConf10Clientes(e.target.checked)}
          data-testid="check-conf-clientes"
          label={
            <>
              Conferi <strong>10 clientes</strong> lado a lado, Omie contra IGM, e os dados
              batem.
            </>
          }
          hint="Use as telas Clientes e Registro de envios ao Omie pra comparar."
        />

        <Checkbox
          checked={conf10Produtos}
          onChange={(e) => setConf10Produtos(e.target.checked)}
          data-testid="check-conf-produtos"
          label={
            <>
              Conferi <strong>10 produtos</strong> do catálogo único contra o Omie, e os dados
              batem.
            </>
          }
          hint="Use a tela Catálogo único de produtos pra comparar."
        />

        <div>
          <Label htmlFor="ativar-texto-confirmacao">Texto de confirmação</Label>
          <Input
            id="ativar-texto-confirmacao"
            type="text"
            value={type2Text}
            onChange={(e) => setType2Text(e.target.value)}
            data-testid="input-type2"
            placeholder={EXPECTED_TYPE2_TEXT}
            className="font-mono"
          />
          <p className="mt-1.5 text-xs text-ink-3 dark:text-ink-dark-3">
            Digite exatamente, em maiúsculas:{' '}
            <code className="break-words">{EXPECTED_TYPE2_TEXT}</code>. Serve pra ninguém
            liberar o envio sem querer.
          </p>
        </div>

        <div>
          <Label htmlFor="ativar-codigo-verificacao">
            Código de verificação em duas etapas
          </Label>
          <Input
            id="ativar-codigo-verificacao"
            type="text"
            inputMode="numeric"
            value={totp}
            onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            data-testid="input-totp"
            placeholder="123456"
            wrapperClassName="w-full max-w-[160px]"
            className="font-mono tracking-widest"
            maxLength={6}
          />
          <p className="mt-1.5 text-xs text-ink-3 dark:text-ink-dark-3">
            Os 6 números do seu aplicativo de autenticação. No ambiente de testes,{' '}
            <code>123456</code> é aceito.
          </p>
        </div>

        <Button onClick={submit} loading={submitting} data-testid="submit-ativar">
          {submitting ? 'Conferindo…' : 'Conferir se está tudo pronto'}
        </Button>
      </Card>

      {result !== null ? (
        <Alert variant="success" title="Está tudo pronto" data-testid="result-success">
          A conferência passou. Agora peça a quem cuida do servidor pra mudar{' '}
          <code>OMIE_WRITE_ENABLED</code> para <code>on</code> no arquivo <code>.env</code> —
          só depois disso o IGM começa a gravar no Omie.
          <details className="mt-3">
            <summary className="cursor-pointer text-xs font-semibold">
              Detalhes técnicos
            </summary>
            <pre className="mt-2 overflow-x-auto text-xs">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </Alert>
      ) : null}

      {err && (
        <Alert variant="danger" title="Ainda não dá pra liberar o envio" data-testid="result-error">
          <span className="break-words">
            {err.message ?? 'Alguma das condições acima não foi cumprida. Revise e tente de novo.'}
          </span>
          <details className="mt-3">
            <summary className="cursor-pointer text-xs font-semibold">Detalhes técnicos</summary>
            <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words text-xs">
              {[err.error, err.code].filter(Boolean).join(' · ')}
            </pre>
          </details>
        </Alert>
      )}
    </div>
  );
}
